import React, { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { MessageSquare, HelpCircle, CalendarDays, MapPin, X, Compass } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { PostCard, MarketplaceFeedCard, CreatePostDialog, LocationPickerDialog, SupportCard, DealsDiscoveryCard, supportCardEligible } from "@/components/community";

const SCOPES = [
  { id: "for_you", label: "For You" },
  { id: "local", label: "Local" },
  { id: "australia", label: "Australia" },
];

const RESERVED = new Set(["mtaa", "user", "guest", "member", "there", ""]);

function greetingWord() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  return "Good evening";
}

function firstNameOf(user) {
  let n = (user?.first_name || "").trim();
  if (!n && user?.name) n = user.name.trim().split(/\s+/)[0];
  if (!n || RESERVED.has(n.toLowerCase())) return "";
  return n;
}

export default function Home() {
  const { user } = useAuth();
  const [scope, setScope] = useState("for_you");
  const [data, setData] = useState(null);
  const [composer, setComposer] = useState(null);
  const [locOpen, setLocOpen] = useState(false);
  const [dismissPrompt, setDismissPrompt] = useState(false);
  const [showSupport, setShowSupport] = useState(supportCardEligible());

  const load = useCallback(() => {
    setData(null);
    api.get("/feed", { params: { scope } }).then(({ data }) => setData(data)).catch(() => setData({ items: [] }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const first = firstNameOf(user);
  const locLabel = user?.city ? `${user.city}, ${user.state}` : user?.state || "Set your location";

  const QUICK = [
    { t: "post", label: "Post", icon: MessageSquare },
    { t: "ask", label: "Ask", icon: HelpCircle },
    { t: "sell", label: "Sell", icon: null, emoji: "🏷️", to: "/post/sell" },
    { t: "event", label: "Event", icon: CalendarDays },
  ];

  const emptyMeta = (() => {
    if (scope === "local") return {
      title: "No posts in your Mtaa yet.",
      subtitle: user?.city ? `Be the first to start something in ${user.city}.` : "Set your community to see what's happening locally.",
    };
    if (scope === "australia") return {
      title: "Nothing across Mtaa yet.",
      subtitle: "Be the first to share something with the community nationwide.",
    };
    return {
      title: "Your Mtaa is just getting started.",
      subtitle: "Create a post, ask a question or explore Australia-wide activity.",
    };
  })();

  const emptyActions = (
    <div className="flex flex-wrap justify-center gap-2">
      <Button onClick={() => setComposer("post")} data-testid="empty-create-post">Create a post</Button>
      <Button variant="outline" onClick={() => setComposer("ask")} data-testid="empty-ask">Ask a question</Button>
      {scope !== "australia" && <Button variant="ghost" onClick={() => setScope("australia")} data-testid="empty-explore"><Compass className="h-4 w-4" />Explore Australia</Button>}
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {user === false && <Navigate to="/explore" replace />}
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight" data-testid="home-greeting">
          {first ? `${greetingWord()}, ${first} 👋🏾` : `${greetingWord()} 👋🏾`}
        </h1>
        <button onClick={() => setLocOpen(true)} data-testid="home-location"
          className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary">
          <MapPin className="h-3.5 w-3.5 text-brand-red" />{locLabel}
        </button>
      </div>

      {user && !user.city && !dismissPrompt && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-brand-green/30 bg-brand-green/5 px-4 py-2.5 text-sm" data-testid="city-prompt">
          <span>Set your community to see your local Mtaa. <button onClick={() => setLocOpen(true)} className="font-semibold text-brand-green underline">Choose location</button></span>
          <button onClick={() => setDismissPrompt(true)} data-testid="dismiss-city-prompt"><X className="h-4 w-4 text-muted-foreground" /></button>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card px-3 py-2.5">
        <p className="px-1 pb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">What's happening in your Mtaa?</p>
        <div className="grid grid-cols-4 gap-2">
          {QUICK.map((q) => { const Icon = q.icon; const inner = (
            <div className="flex flex-col items-center gap-1 rounded-xl border border-border py-2 text-xs font-medium transition-colors hover:border-primary hover:text-primary" data-testid={`quick-${q.t}`}>
              {Icon ? <Icon className="h-[18px] w-[18px]" /> : <span className="text-base leading-none">{q.emoji}</span>}{q.label}
            </div>
          );
            return q.to ? <Link key={q.t} to={q.to}>{inner}</Link> : <button key={q.t} onClick={() => setComposer(q.t)}>{inner}</button>;
          })}
        </div>
      </div>

      <div className="flex gap-1.5">
        {SCOPES.map((s) => (
          <button key={s.id} onClick={() => setScope(s.id)} data-testid={`scope-${s.id}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${scope === s.id ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>{s.label}</button>
        ))}
      </div>

      {!data ? <div className="flex justify-center py-16"><Spinner className="h-7 w-7" /></div> : data.items.length === 0 ? (
        <>
          <EmptyState icon={MessageSquare} title={emptyMeta.title} subtitle={emptyMeta.subtitle} action={emptyActions} testid={`empty-${scope}`} />
          {showSupport && <SupportCard onDismiss={() => setShowSupport(false)} />}
        </>
      ) : (
        <div className="space-y-4">
          {data.items.map((it, idx) => (
            <React.Fragment key={it.kind === "listing" ? `l-${it.id}` : `p-${it.id}`}>
              {it.kind === "listing"
                ? <MarketplaceFeedCard item={it} />
                : <PostCard item={it} onChanged={load} onDeleted={load} />}
              {scope === "for_you" && idx === Math.min(1, data.items.length - 1) && <DealsDiscoveryCard />}
              {showSupport && (idx === Math.min(4, data.items.length - 1)) && (
                <SupportCard onDismiss={() => setShowSupport(false)} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <CreatePostDialog open={!!composer} onOpenChange={(o) => !o && setComposer(null)} type={composer || "post"} onDone={load} />
      <LocationPickerDialog open={locOpen} onOpenChange={setLocOpen} onSaved={load} />
    </div>
  );
}
